import { CONTENT_ASSETS, CONTENT_TEMPLATE_PRESETS, STORED_ASSET_ID, WORKSPACE_DOCUMENT_LIMITS, editorialTextRole, isStoredAsset, validateContentDocument } from './document.mjs';
import { isKnownStoredAsset, storedAssetBlob } from './stored-assets.mjs';
import { renderContentDocument } from './renderer.mjs';
import { componentBlockSpec, createBlockSuiteComponents, autoTextWidth } from './component-blocksuite.mjs';

const VENDOR = '/vendor/blocksuite-0.19.5/';
const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const MAX_IMAGE_BYTES = WORKSPACE_DOCUMENT_LIMITS.assetBytes;
const MAX_SCENE_IMAGE_BYTES = WORKSPACE_DOCUMENT_LIMITS.sectionAssetBytes;
const SUPPORTED_BLOCKS = new Set(['affine:page', 'affine:surface', 'affine:note', 'affine:paragraph', 'affine:list', 'affine:image', 'affine:divider', 'affine:code', 'affine:frame', 'affine:edgeless-text', 'affine:latex']);
let runtimePromise;
async function runtime() {
  if (!runtimePromise) runtimePromise = (async () => {
    if (!document.querySelector('link[data-blocksuite-runtime]')) {
      const css = document.createElement('link');
      css.rel = 'stylesheet'; css.href = `${VENDOR}blocksuite.css`; css.dataset.blocksuiteRuntime = '';
      document.head.append(css);
    }
    if (!document.querySelector('style[data-blocksuite-empty]')) {
      const style = document.createElement('style'); style.dataset.blocksuiteEmpty = '';
      style.textContent = '[data-blocksuite-empty="true"][data-blocksuite-mode="page"] affine-paragraph .affine-paragraph-placeholder{display:block;color:#687486}';
      document.head.append(style);
    }
    return import(`${VENDOR}blocksuite.js`);
  })();
  return runtimePromise;
}
// Penpot's plugin runtime hardens shared intrinsics. BlockSuite's ES module
// initializes prototypes that inherit from them, so load it first in the
// shared editor window, before Penpot starts.
export function preloadBlockSuiteRuntime() { return runtime(); }
let contentStylesPromise;
function contentStyles() {
  if (!contentStylesPromise) contentStylesPromise = (async () => {
    const href = new URL('./content-studio.css', import.meta.url).href;
    if ([...document.styleSheets].some(sheet => sheet.href === href)) return;
    const existing = [...document.querySelectorAll('link[rel="stylesheet"]')].find(sheet => sheet.href === href);
    const sheet = existing || document.createElement('link');
    if (sheet.sheet) return;
    await new Promise((resolve, reject) => {
      sheet.addEventListener('load', resolve, { once: true });
      sheet.addEventListener('error', () => reject(new Error('콘텐츠 글꼴 서식을 불러오지 못했습니다.')), { once: true });
      if (!existing) { sheet.rel = 'stylesheet'; sheet.href = href; document.head.append(sheet); }
    });
  })().catch(error => { contentStylesPromise = undefined; throw error; });
  return contentStylesPromise;
}
async function canvasFonts(elements) {
  const fonts = new Map();
  for (const element of elements) {
    if (element.type !== 'text' || !element.fontFamily) continue;
    const family = element.fontFamily;
    // BlockSuite's own font tokens are resolved by its FontLoader, not CSS FontFaceSet.
    if (family.startsWith('blocksuite:')) continue;
    const cssFamily = /[,"']/.test(family) ? family : JSON.stringify(family);
    const font = `${element.fontStyle || 'normal'} ${element.fontWeight || '400'} ${element.fontSize || 16}px ${cssFamily}`;
    fonts.set(font, family.includes('Content KoPub'));
  }
  if ([...fonts.values()].some(Boolean)) await contentStyles();
  await Promise.all([...fonts].map(async ([font, required]) => {
    const loaded = await document.fonts.load(font, '한글 Aa');
    if (required && !loaded.length) throw new Error(`콘텐츠 글꼴을 불러오지 못했습니다: ${font}`);
  }));
}
const clone = value => JSON.parse(JSON.stringify(value));
function textDelta(delta) {
  if (!Array.isArray(delta)) throw new Error('텍스트는 insert 배열이어야 합니다.');
  return delta.map(part => {
    if (!part || typeof part.insert !== 'string' || Object.keys(part).some(key => !['insert', 'attributes'].includes(key))) throw new Error('텍스트 insert만 지원합니다.');
    const result = { insert: part.insert.replaceAll('\r\n', '\n') };
    if (part.attributes !== undefined) {
      if (!part.attributes || typeof part.attributes !== 'object' || Array.isArray(part.attributes)) throw new Error('텍스트 서식이 올바르지 않습니다.');
      const attributes = {};
      for (const [key, value] of Object.entries(part.attributes)) {
        if (['bold', 'italic', 'underline', 'strike', 'code'].includes(key) && typeof value === 'boolean') attributes[key] = value;
        else if (['color', 'background'].includes(key) && typeof value === 'string' && CSS.supports('color', value)) attributes[key] = value;
        else if (key === 'link' && typeof value === 'string' && /^(https?:|mailto:)/i.test(value)) attributes[key] = value;
        else throw new Error(`지원하지 않는 텍스트 서식: ${key}`);
      }
      if (Object.keys(attributes).length) result.attributes = attributes;
    }
    return result;
  }).filter(part => part.insert.length);
}
function textStyle(options = {}) {
  const style = {};
  for (const key of ['color', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'textAlign']) {
    if (options[key] === undefined) continue;
    const value = options[key];
    const valid = key === 'fontSize' ? Number.isFinite(value) && value >= 8 && value <= 200
      : key === 'color' ? typeof value === 'string' && CSS.supports('color', value)
      : key === 'fontFamily' ? typeof value === 'string' && value.length > 0 && value.length <= 120 && !/[;{}<>]/.test(value)
      : key === 'fontWeight' ? ['normal', 'bold', '100', '200', '300', '400', '500', '600', '700', '800', '900'].includes(String(value))
      : key === 'fontStyle' ? ['normal', 'italic', 'oblique'].includes(value)
      : ['left', 'center', 'right'].includes(value);
    if (!valid) throw new Error(`지원하지 않는 텍스트 속성: ${key}`);
    style[key] = key === 'fontWeight' ? String(value) : value;
  }
  return style;
}
const bytesToBase64 = bytes => {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 32768) binary += String.fromCharCode(...bytes.subarray(i, i + 32768));
  return btoa(binary);
};
function references(block, ids = new Set()) {
  if (block.flavour === 'affine:image' && block.props.sourceId) ids.add(block.props.sourceId);
  for (const child of block.children) references(child, ids);
  return ids;
}
async function makeDocument(B, snapshot, assets, mode) {
  const schemas = B.AffineSchemas.filter(schema => SUPPORTED_BLOCKS.has(schema.model.flavour)).map(schema => {
    if (schema.model.flavour === 'affine:note') return {...schema, model:{...schema.model, children:[...schema.model.children, 'edusense:component', 'edusense:scene']}};
    // The page keeps its data components' records and, on a Saju book page, the person its printed text was written for (book-text.mjs).
    if (schema.model.flavour === 'affine:page') return {...schema, model:{...schema.model, props:internal => ({...schema.model.props(internal), edusenseComponents:[], edusenseBookText:null})}};
    return schema;
  });
  const collection = new B.DocCollection({ schema: new B.Schema().register([...schemas, ...componentBlockSpec(B).schemas]) });
  collection.start(); collection.meta.initialize();
  try {
    let doc;
    if (snapshot) {
      const job = new B.Job({ collection });
      let total = 0;
      for (const asset of assets) if (!IMAGE_TYPES.has(asset.mime)) throw new Error(`지원하지 않는 이미지 형식: ${asset.mime}`);
      // A picture kept in the asset store is fetched (once per session); one held in the page is decoded.
      const blobs = await Promise.all(assets.map(asset => isStoredAsset(asset) ? storedAssetBlob(asset.id)
        : new Blob([Uint8Array.from(atob(asset.data), c => c.charCodeAt(0))], { type: asset.mime })));
      assets.forEach((asset, index) => {
        if (blobs[index].size > MAX_IMAGE_BYTES) throw new Error('이미지는 개당 4 MiB 이하여야 합니다.');
        total += blobs[index].size;
        if (total > MAX_SCENE_IMAGE_BYTES) throw new Error('한 쪽의 이미지 합계는 8 MiB 이하여야 합니다.');
        job.assets.set(asset.id, blobs[index]);
      });
      for (const id of references(snapshot.blocks)) if (!job.assets.has(id)) throw new Error(`이미지 파일이 없습니다: ${id}`);
      doc = await job.snapshotToDoc(clone(snapshot));
      if (!doc) throw new Error('BlockSuite 문서를 복원하지 못했습니다.');
      collection.meta.setDocMeta(doc.id, { title: snapshot.meta.title, createDate: snapshot.meta.createDate });
    } else {
      doc = collection.createDoc(); doc.load();
      const page = doc.addBlock('affine:page', { title: new B.Text('') });
      doc.addBlock('affine:surface', {}, page);
      if (mode === 'page') {
        const note = doc.addBlock('affine:note', {}, page);
        doc.addBlock('affine:paragraph', { text: new B.Text('') }, note);
      }
    }
    // Native canvas text uses the same surface model as the layer and typography editors.
    doc.awarenessStore.setFlag('enable_edgeless_text', false);
    doc.resetHistory();
    return { collection, doc };
  } catch (error) { collection.dispose(); throw error; }
}

const SCENE_TYPES = new Set(['frame', 'shape', 'text', 'image']);
const SCENE_SHAPES = new Set(['rect', 'ellipse', 'triangle', 'diamond']);
const snapshotSurface = snapshot => snapshot.blocks.children.find(block => block.flavour === 'affine:surface');
function assertPortableSnapshot(snapshot) {
  const visit = block => {
    if (['affine:page', 'affine:surface', 'affine:frame', 'affine:image'].includes(block.flavour)) {
      for (const child of block.children) visit(child);
      return;
    }
    // A document-mode visit creates an empty paragraph. It carries no authored content.
    if (block.flavour === 'affine:note' && block.children.every(child => child.flavour === 'affine:paragraph' && !(child.props.text?.delta || []).some(op => op.insert))) return;
    throw new Error('이 페이지의 문서 블록은 아직 Penpot으로 옮길 수 없습니다. 문서 원본은 유지됩니다.');
  };
  visit(snapshot.blocks);
  for (const model of Object.values(snapshotSurface(snapshot).props.elements)) {
    if (!['shape', 'text'].includes(model.type)) throw new Error(`아직 전환할 수 없는 캔버스 요소입니다: ${model.type}`);
    if (model.type === 'shape' && (!SCENE_SHAPES.has(model.shapeType) || model.shapeStyle !== 'General' || model.shadow || model.strokeStyle !== 'solid' || (model.text?.delta || []).some(op => op.insert))) throw new Error('손그림 도형, 도형 안의 텍스트 또는 이 도형 종류는 아직 전환할 수 없습니다.');
  }
}
export function portableScene(snapshot, assets = [], resolveColor = color => color) {
  assertPortableSnapshot(snapshot);
  const surface = snapshotSurface(snapshot), elements = [];
  for (const [id, model] of Object.entries(surface.props.elements)) {
    const [x, y, width, height] = JSON.parse(model.xywh);
    const element = { id, type:model.type, x, y, width, height, rotate:model.rotate || 0, locked:Boolean(model.lockedBySelf), index:model.index };
    if (model.type === 'text') {
      element.delta = clone(model.text.delta); element.text = element.delta.map(op => op.insert).join('');
      if (model.hasMaxWidth === false) element.autoWidth = true;
      for (const key of ['color', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'textAlign']) element[key] = model[key];
    } else for (const key of ['shapeType', 'fillColor', 'strokeColor', 'strokeWidth', 'filled', 'radius']) element[key] = model[key];
    elements.push(element);
  }
  const visit = block => {
    if (['affine:frame', 'affine:image'].includes(block.flavour)) {
      const props = block.props, [x, y, width, height] = JSON.parse(props.xywh);
      const element = { id:block.id, type:block.flavour === 'affine:frame' ? 'frame' : 'image', x, y, width, height, rotate:props.rotate || 0, locked:Boolean(props.lockedBySelf), index:props.index };
      if (element.type === 'frame') { element.title = props.title.delta.map(op => op.insert).join(''); element.fillColor = props.background; }
      else { element.assetId = props.sourceId; element.caption = props.caption || ''; }
      elements.push(element);
    }
    for (const child of block.children) visit(child);
  };
  visit(snapshot.blocks);
  const frameParents = new Map();
  for (const frame of surface.children.filter(block => block.flavour === 'affine:frame')) {
    for (const [id, member] of Object.entries(frame.props.childElementIds || {})) if (member) {
      if (frameParents.has(id) && frameParents.get(id) !== frame.id) throw new Error('여러 보드에 중복된 요소는 아직 전환할 수 없습니다.');
      frameParents.set(id, frame.id);
    }
  }
  for (const element of elements) {
    if (frameParents.has(element.id)) element.parentId = frameParents.get(element.id);
    for (const key of ['color', 'fillColor', 'strokeColor']) if (typeof element[key] === 'string') element[key] = resolveColor(element[key]);
  }
  elements.sort((a, b) => a.type === 'frame' && b.type !== 'frame' ? -1 : b.type === 'frame' && a.type !== 'frame' ? 1 : a.index < b.index ? -1 : a.index > b.index ? 1 : 0);
  return { version:1, title:snapshot.meta.title, elements:elements.map(({index, ...element}) => element), assets:clone(assets) };
}
const sceneAssetBytes = asset => isStoredAsset(asset) ? asset.size : atob(asset.data).length;
function validateScene(scene) {
  if (!scene || scene.version !== 1 || typeof scene.title !== 'string' || !Array.isArray(scene.elements) || scene.elements.length > 2000 || !Array.isArray(scene.assets)) throw new Error('편집기 전환 데이터가 올바르지 않습니다.');
  const ids = new Set(), assetIds = new Set(); let total = 0;
  for (const asset of scene.assets) {
    const stored = isStoredAsset(asset);
    if (!asset || typeof asset.id !== 'string' || !asset.id || assetIds.has(asset.id) || !IMAGE_TYPES.has(asset.mime)
      || (stored ? !STORED_ASSET_ID.test(asset.id) || !Number.isInteger(asset.size) || asset.size < 1 : typeof asset.data !== 'string')) throw new Error('편집기 전환 이미지가 올바르지 않습니다.');
    const bytes = sceneAssetBytes(asset); total += bytes;
    if (bytes > MAX_IMAGE_BYTES || total > MAX_SCENE_IMAGE_BYTES) throw new Error('한 쪽의 이미지는 개당 4 MiB, 합계 8 MiB 이하여야 합니다.');
    assetIds.add(asset.id);
  }
  for (const element of scene.elements) {
    if (!element || typeof element.id !== 'string' || !element.id || element.id.length > 200 || ids.has(element.id) || !SCENE_TYPES.has(element.type)) throw new Error('전환할 요소의 ID 또는 종류가 올바르지 않습니다.');
    ids.add(element.id);
    for (const key of ['x', 'y', 'width', 'height']) if (!Number.isFinite(element[key]) || Math.abs(element[key]) > 1000000 || (['width', 'height'].includes(key) && element[key] <= 0)) throw new Error('전환할 요소의 크기 또는 위치가 올바르지 않습니다.');
    if (element.rotate !== undefined && (!Number.isFinite(element.rotate) || Math.abs(element.rotate) > 360)) throw new Error('전환할 요소의 회전이 올바르지 않습니다.');
    if (element.type === 'frame' && element.rotate) throw new Error('회전한 보드는 아직 BlockSuite로 전환할 수 없습니다.');
    if (element.type === 'image' && !assetIds.has(element.assetId)) throw new Error(`전환할 이미지 원본이 없습니다: ${element.assetId}`);
    if (element.type === 'text') {
      textDelta(element.delta || [{insert:element.text}]); textStyle(element);
      if (element.autoWidth !== undefined && typeof element.autoWidth !== 'boolean') throw new Error('텍스트 자동 너비 설정이 올바르지 않습니다.');
    }
    if (element.type === 'shape') {
      if (!SCENE_SHAPES.has(element.shapeType)) throw new Error('아직 전환할 수 없는 도형 종류입니다.');
      if (!Number.isFinite(element.strokeWidth) || element.strokeWidth < 0 || element.strokeWidth > 100) throw new Error('도형 선 두께가 올바르지 않습니다.');
      if (element.radius !== undefined && (!Number.isFinite(element.radius) || element.radius < 0 || element.radius > 1)) throw new Error('도형 모서리가 올바르지 않습니다.');
    }
    for (const key of ['fillColor', 'strokeColor']) if (element[key] !== undefined && !(typeof element[key] === 'string' && CSS.supports('color', element[key]))) throw new Error(`전환할 색상이 올바르지 않습니다: ${key}`);
  }
  for (const element of scene.elements) if (element.parentId !== undefined && (!scene.elements.some(parent => parent.id === element.parentId && parent.type === 'frame') || element.parentId === element.id)) throw new Error('편집 요소의 부모 보드가 올바르지 않습니다.');
}
async function serializeDocument(B, collection, doc) {
  const snapshot = new B.Job({ collection }).docToSnapshot(doc);
  if (!snapshot) throw new Error('BlockSuite 문서 내보내기에 실패했습니다.');
  const assets = []; let total = 0;
  for (const id of references(snapshot.blocks)) {
    const blob = await collection.blobSync.get(id);
    if (!blob) throw new Error(`이미지 파일이 없습니다: ${id}`);
    if (!IMAGE_TYPES.has(blob.type) || blob.size > MAX_IMAGE_BYTES) throw new Error('PNG/JPEG/WebP/GIF 이미지는 개당 4 MiB 이하여야 합니다.');
    total += blob.size;
    if (total > MAX_SCENE_IMAGE_BYTES) throw new Error('한 쪽의 이미지 합계는 8 MiB 이하여야 합니다.');
    assets.push(isKnownStoredAsset(id) ? { id, mime:blob.type, size:blob.size }
      : { id, mime:blob.type, data:bytesToBase64(new Uint8Array(await blob.arrayBuffer())) });
  }
  const saved = clone(snapshot);
  // Only a Saju book page records whose text it holds; any other page is saved as before.
  if (saved.blocks.props.edusenseBookText === null) delete saved.blocks.props.edusenseBookText;
  return { snapshot:saved, assets };
}

/**
 * Transfer editable objects through the native models, retaining existing object IDs and fields.
 * `bookText` ({ product, subject }) names the person a Saju book page's printed text was written for (book-text.mjs).
 * `components` are data components placed on the new page ({instanceId, scene, record, placement}):
 * their objects are native like the rest, and the page keeps their records (component-blocksuite.mjs).
 */
export async function fromScene(scene, { previous, components = [], bookText = null } = {}) {
  validateScene(scene);
  if (previous) assertPortableSnapshot(previous.snapshot);
  await canvasFonts(scene.elements);
  const B = await runtime();
  const { collection, doc } = await makeDocument(B, previous?.snapshot, previous?.assets || [], 'edgeless');
  try {
    const surface = doc.getBlocksByFlavour('affine:surface')[0].model;
    const wanted = new Map(scene.elements.map(element => [element.id, element]));
    const renamed = new Map();
    for (const element of scene.elements) if ([doc.root.id, surface.id].includes(element.id)) throw new Error('편집 요소 ID가 문서 ID와 겹칩니다.');
    for (const asset of scene.assets) await collection.blobSync.set(asset.id, isStoredAsset(asset) ? await storedAssetBlob(asset.id)
      : new Blob([Uint8Array.from(atob(asset.data), c => c.charCodeAt(0))], {type:asset.mime}));
    for (const model of [...surface.elementModels]) if (wanted.get(model.id)?.type !== model.type) surface.deleteElement(model.id);
    for (const flavour of ['affine:frame', 'affine:image']) for (const {model} of [...doc.getBlocksByFlavour(flavour)]) if (wanted.get(model.id)?.type !== flavour.slice(7)) doc.deleteBlock(model);
    let index = null;
    for (const element of scene.elements) {
      index = B.generateKeyBetweenV2(index, null);
      const props = { xywh:JSON.stringify([element.x, element.y, element.width, element.height]), rotate:element.rotate || 0, lockedBySelf:Boolean(element.locked), index };
      if (element.type === 'text' || element.type === 'shape') {
        if (element.type === 'text') {
          const delta = textDelta(element.delta || [{insert:element.text}]);
          const text = new B.Y.Text(); text.applyDelta(delta);
          Object.assign(props, textStyle(element), {text, hasMaxWidth:element.autoWidth !== true});
          // An explicitly unwrapped source line must fit the loaded browser font as well (autoTextWidth).
          if (element.autoWidth) props.xywh = JSON.stringify([element.x, element.y, autoTextWidth({...element, text:delta.map(part => part.insert).join('')}), element.height]);
        } else Object.assign(props, {shapeType:element.shapeType, shapeStyle:'General', fillColor:element.fillColor, strokeColor:element.strokeColor, strokeWidth:element.strokeWidth, filled:element.filled, radius:element.radius || 0});
        const model = surface.getElementById(element.id);
        if (model) surface.updateElement(model.id, props);
        else renamed.set(surface.addElement({...props, type:element.type}), element.id);
      } else {
        if (element.type === 'frame') Object.assign(props, {title:new B.Text(element.title || ''), background:element.fillColor});
        else Object.assign(props, {sourceId:element.assetId, width:element.width, height:element.height, size:sceneAssetBytes(scene.assets.find(asset => asset.id === element.assetId)), caption:element.caption || ''});
        const model = doc.getBlockById(element.id);
        if (model) doc.updateBlock(model, props);
        else doc.addBlock(`affine:${element.type}`, {...props, id:element.id}, surface.id);
      }
    }
    doc.updateBlock(doc.root, {title:new B.Text(scene.title), ...(bookText ? {edusenseBookText:clone(bookText)} : {})});
    collection.meta.setDocMeta(doc.id, {title:scene.title});
    if (components.length) {
      const api = createBlockSuiteComponents({B, doc, surface, note:() => doc.getBlocksByFlavour('affine:note')[0]?.model, editable:() => {}, mode:() => 'edgeless', fonts:canvasFonts,
        indexes:() => [...surface.elementModels.map(model => model.index), ...['affine:frame', 'affine:image'].flatMap(flavour => doc.getBlocksByFlavour(flavour).map(({model}) => model.index))]});
      for (const component of components) (await api.upsertComponent(component)).commit();
    }
    const result = await serializeDocument(B, collection, doc);
    // Surface.addElement intentionally generates IDs. Normalize only new native snapshot IDs;
    // Job.snapshotToDoc restores them unchanged, preserving identity across both editors.
    const elements = snapshotSurface(result.snapshot).props.elements;
    for (const [generated, id] of renamed) { elements[id] = {...elements[generated], id}; delete elements[generated]; }
    const records = result.snapshot.blocks.props.edusenseComponents;
    for (const frame of snapshotSurface(result.snapshot).children.filter(block => block.flavour === 'affine:frame')) {
      frame.props.childElementIds = Object.fromEntries([...scene.elements.filter(element => element.parentId === frame.id).map(element => element.id),
        ...records.filter(record => record.binding.placement?.frameId === frame.id).flatMap(record => Object.values(record.binding.elements).map(entry => entry.id))].map(id => [id, true]));
    }
    return components.length ? {...result, components:clone(records)} : result;
  } finally { collection.dispose(); }
}

/** One section owns one native document; images travel with Job's JSON snapshot. */
export async function mount(host, { mode = 'page', snapshot, assets = [], readonly = false, pageBounds = null, onChange, onSelectionChange } = {}) {
  if (!(host instanceof HTMLElement)) throw new TypeError('편집기 컨테이너가 필요합니다.');
  if (!['page', 'edgeless'].includes(mode)) throw new Error('지원하지 않는 편집 모드입니다.');
  if (snapshot) {
    const elements = Object.values(snapshotSurface(snapshot).props.elements);
    const visit = block => { if (['edusense:component', 'edusense:scene'].includes(block.flavour)) elements.push(...block.props.scene.elements); block.children.forEach(visit); };
    visit(snapshot.blocks);
    await canvasFonts(elements);
  }
  const B = await runtime();
  const { collection, doc } = await makeDocument(B, snapshot, assets, mode);
  let editor, disposed = false, resizeTimer, selectionSubscription, sequence = 0;
  let canvasPreview, canvasContainer, canvasQuery, canvasQueryKey, previewTimer;
  const surface = doc.getBlocksByFlavour('affine:surface')[0]?.model;
  if (!surface) { collection.dispose(); throw new Error('문서에 캔버스 데이터가 없습니다.'); }
  const editable = () => { if (disposed) throw new Error('닫힌 편집기입니다.'); if (readonly) throw new Error('읽기 전용 문서입니다.'); };
  const note = () => {
    const existing = doc.getBlocksByFlavour('affine:note')[0]?.model;
    return existing || doc.getBlockById(doc.addBlock('affine:note', {}, doc.root.id));
  };
  const gfx = () => editor.std.get(B.GfxController);
  const index = () => mode === 'edgeless' ? gfx().layer.generateIndex() : B.generateKeyBetweenV2(null, null);
  const position = (width = 280, height = 100, options = {}) => {
    if (options.xywh) return options.xywh;
    const x = 80 + (sequence % 4) * 45, y = 80 + (sequence % 6) * 45; sequence++;
    return JSON.stringify([x, y, width, height]);
  };
  const updateEmptyState = () => {
    const paragraphs = doc.getBlocksByFlavour('affine:paragraph');
    const notes = doc.getBlocksByFlavour('affine:note');
    host.dataset.blocksuiteEmpty = String(mode === 'page' && !readonly && paragraphs.length === 1 && paragraphs[0].model.text.length === 0 && notes.length === 1 && notes[0].model.children.length === 1);
  };
  const changed = () => {
    if (disposed) return;
    updateEmptyState();
    clearTimeout(previewTimer);
    previewTimer = setTimeout(() => { if (!disposed && mode === 'page') void syncDocumentCanvas(); }, 0);
    // The owner must observe mutations before saving; it debounces expensive serialization.
    onChange?.();
    onSelectionChange?.();
  };
  function disposeDocumentCanvas() {
    clearTimeout(previewTimer);
    canvasPreview?.remove(); canvasContainer?.remove();
    if (canvasQuery) doc.blockCollection.clearQuery(canvasQuery, true);
    canvasPreview = canvasContainer = canvasQuery = canvasQueryKey = undefined;
  }
  async function syncDocumentCanvas() {
    if (disposed || mode !== 'page') return;
    const canvasNotes = doc.getBlocksByFlavour('affine:note').filter(({model}) => model.displayMode === 'edgeless');
    if (!surface.elementModels.length && !surface.children.length && !canvasNotes.length && !doc.getBlocksByFlavour('affine:edgeless-text').length) {
      disposeDocumentCanvas(); return;
    }
    const hiddenIds = [];
    const hide = model => { hiddenIds.push(model.id); model.children.forEach(hide); };
    for (const {model} of doc.getBlocksByFlavour('affine:note')) if (model.displayMode !== 'edgeless') hide(model);
    const key = JSON.stringify(hiddenIds.sort());
    if (!canvasPreview || canvasQueryKey !== key) {
      disposeDocumentCanvas(); canvasQueryKey = key;
      canvasQuery = { mode: 'loose', match: hiddenIds.map(id => ({ id, viewType: B.BlockViewType.Hidden })) };
      canvasContainer = document.createElement('div');
      canvasContainer.dataset.blocksuiteDocumentCanvas = '';
      canvasContainer.setAttribute('aria-label', '페이지 디자인');
      canvasContainer.style.cssText = 'width:100%;max-width:900px;margin:0 auto;overflow:hidden;';
      canvasPreview = new B.EdgelessEditor();
      canvasPreview.specs = [...B.PreviewEdgelessEditorBlockSpecs, ...componentBlockSpec(B).extensions];
      canvasPreview.doc = collection.getDoc(doc.id, { readonly: true, query: canvasQuery });
      canvasPreview.style.cssText = 'display:block;width:100%;height:100%;pointer-events:none;';
      canvasContainer.append(canvasPreview);
      editor.querySelector('.affine-page-viewport').prepend(canvasContainer);
      const mounted = canvasPreview;
      await mounted.updateComplete;
      if (disposed || mounted !== canvasPreview) return;
    }
    fitDocumentCanvas();
  }
  function fitDocumentCanvas() {
    if (!canvasPreview || !canvasPreview.std || disposed) return;
    const controller = canvasPreview.std.get(B.GfxController);
    const elements = controller.gfxElements.filter(model => model.flavour !== 'affine:note' || model.displayMode === 'edgeless');
    if (!elements.length) return;
    const bound = B.getCommonBoundWithRotation(elements);
    const width = canvasContainer.clientWidth;
    if (width <= 0 || bound.w <= 0 || bound.h <= 0) return;
    const padding = 24;
    canvasContainer.style.height = `${Math.max(160, (width - padding * 2) * bound.h / bound.w + padding * 2)}px`;
    controller.viewport.setViewportByBound(bound, [padding, padding, padding, padding], false);
  }
  async function setMode(nextMode) {
    if (!['page', 'edgeless'].includes(nextMode)) throw new Error('지원하지 않는 편집 모드입니다.');
    if (disposed) throw new Error('닫힌 편집기입니다.');
    mode = nextMode;
    // Canvas-only snapshots still need a real editable paragraph when opened as a document.
    if (mode === 'page' && !readonly && !doc.getBlocksByFlavour('affine:note').length) doc.addBlock('affine:paragraph', { text: new B.Text('') }, note().id);
    selectionSubscription?.dispose();
    selectionSubscription = undefined;
    disposeDocumentCanvas();
    editor?.remove();
    editor = mode === 'edgeless' ? new B.EdgelessEditor() : new B.PageEditor();
    editor.specs = [...(mode === 'edgeless' ? B.EdgelessEditorBlockSpecs : B.PageEditorBlockSpecs), ...componentBlockSpec(B).extensions];
    editor.doc = readonly ? collection.getDoc(doc.id, { readonly: true }) : doc;
    editor.style.cssText = `display:block;width:100%;height:100%;min-height:${pageBounds ? 0 : 420}px;`;
    if (pageBounds) editor.style.pointerEvents = 'none';
    if (readonly && mode === 'edgeless' && host.matches('[data-native-section][data-kind="canvas"]')) editor.style.pointerEvents = 'none';
    host.dataset.theme = 'light'; host.dataset.blocksuiteMode = mode; host.append(editor);
    await editor.updateComplete;
    if (mode === 'edgeless') selectionSubscription = gfx().selection.slots.updated.on(() => { if (!disposed) onSelectionChange?.(); });
    const paragraphs = editor.std.getService('affine:paragraph');
    if (paragraphs) paragraphs.placeholderGenerator = model => model.type === 'text' ? '여기에 내용을 입력하세요. / 명령으로 제목과 목록을 추가할 수 있습니다.' : `제목 ${model.type.slice(1)}`;
    for (const paragraph of editor.querySelectorAll('affine-paragraph')) paragraph.requestUpdate();
    // The pinned 0.19.5 toolbar exposes tools as getters; omit embed and cloud-template actions.
    const toolbar = editor.querySelector('edgeless-toolbar-widget');
    if (toolbar) {
      for (const [name, keep] of [['_quickTools', tool => Boolean(tool.type)], ['_seniorTools', tool => tool.name !== 'Template']]) {
        let prototype = Object.getPrototypeOf(toolbar), getter;
        while (prototype && !getter) { getter = Object.getOwnPropertyDescriptor(prototype, name)?.get; prototype = Object.getPrototypeOf(prototype); }
        if (getter) Object.defineProperty(toolbar, name, { get: () => getter.call(toolbar).filter(keep) });
      }
      toolbar.requestUpdate();
    }
    const slash = editor.querySelector('affine-slash-menu-widget');
    if (slash) slash.config = { ...slash.config, items: slash.config.items.filter(item => typeof item !== 'function' && !['Page', 'Database', 'Embed'].includes(item.groupName) && !['New Doc', 'Linked Doc', 'Link', 'Attachment', 'Table View', 'Todo', 'Kanban View', 'YouTube', 'GitHub', 'Figma', 'Loom'].includes(item.name)) };
    updateEmptyState();
    if (mode === 'edgeless') fitContent();
    else await syncDocumentCanvas();
    onSelectionChange?.();
  }
  function fitContent() {
    if (disposed) return;
    if (mode === 'page') { fitDocumentCanvas(); return; }
    const controller = gfx();
    if (readonly && pageBounds) {
      const [x, y, w, h] = pageBounds;
      controller.viewport.setViewportByBound({ x, y, w, h }, [0, 0, 0, 0], false);
      return;
    }
    const bound = controller.elementsBound;
    if (bound.w <= 0 || bound.h <= 0) return;
    if (readonly && host.matches('[data-native-section][data-kind="canvas"]')) {
      // Reader pages scroll vertically, so give a tall composition its natural height.
      const padding = 32;
      const width = host.clientWidth;
      if (width <= padding * 2) return;
      const minHeight = window.matchMedia('(max-width: 520px)').matches ? 420 : 560;
      const height = Math.max(minHeight, Math.min(8000, Math.ceil((width - padding * 2) * bound.h / bound.w + padding * 2)));
      host.style.height = `${height}px`;
      controller.viewport.setViewportByBound(bound, [padding, padding, padding, padding], false);
      return;
    }
    // A single frame represents one editable page. Show the whole page on entry;
    // the native toolbar still lets the author zoom in to work on its details.
    const frames = doc.getBlocksByFlavour('affine:frame');
    if (frames.length === 1) {
      const [x, y, w, h] = JSON.parse(frames[0].model.xywh);
      controller.viewport.setViewportByBound({ x, y, w, h }, [50, 50, 100, 50], false);
      return;
    }
    // In the editor, fit the width of unframed portrait work while keeping its lower part
    // reachable with BlockSuite's existing pan and zoom controls.
    const padding = [50, 50, 100, 50];
    const availableWidth = host.clientWidth - padding[1] - padding[3];
    const availableHeight = host.clientHeight - padding[0] - padding[2];
    const visibleHeight = availableWidth > 0 && availableHeight > 0
      ? Math.min(bound.h, bound.w * availableHeight / availableWidth)
      : bound.h;
    const focus = visibleHeight < bound.h ? { x: bound.x, y: bound.y, w: bound.w, h: visibleHeight } : bound;
    controller.viewport.setViewportByBound(focus, padding, true);
  }
  await setMode(mode);
  doc.spaceDoc.on('update', changed);
  let lastSize;
  const resizeObserver = new ResizeObserver(entries => {
    const { width, height } = entries[0].contentRect;
    if (lastSize && Math.abs(width - lastSize.width) < 1 && Math.abs(height - lastSize.height) < 1) return;
    lastSize = { width, height };
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { if (width > 0 && height > 0) fitContent(); }, 80);
  });
  resizeObserver.observe(host);
  const textModel = id => {
    const element = surface.getElementById(id);
    if (element) return element.type === 'text' ? { model: element, yText: element.text, canvas: true } : null;
    const block = doc.getBlockById(id);
    return block && ['affine:paragraph', 'affine:list'].includes(block.flavour) ? { model: block, yText: block.text.yText, canvas: false } : null;
  };
  const layerProperties = {
    text: ['x', 'y', 'width', 'rotate', 'color', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'textAlign'],
    shape: ['x', 'y', 'width', 'height', 'rotate', 'fillColor', 'strokeColor', 'strokeWidth'],
    image: ['x', 'y', 'width', 'height', 'rotate'],
    frame: ['x', 'y', 'width', 'height', 'fillColor'],
    note: ['x', 'y', 'width', 'fillColor'],
    group: ['x', 'y'],
  };
  const layerType = model => model.flavour ? model.flavour.slice('affine:'.length) : model.type;
  const canvasModels = () => mode === 'edgeless' && !disposed
    ? gfx().gfxElements.filter(model => Object.hasOwn(layerProperties, layerType(model)) && !(model.flavour === 'affine:note' && model.displayMode === 'doc'))
    : [];
  const layerModel = id => canvasModels().find(model => model.id === id);
  const describeLayer = model => {
    const type = layerType(model), [x, y, width, height] = JSON.parse(model.xywh);
    let label;
    if (type === 'text') label = model.text.toString();
    else if (type === 'frame' || type === 'group') label = model.title.toString();
    else if (type === 'image') label = model.caption;
    else if (type === 'note') label = model.children.map(child => child.text ? child.text.toString() : '').join(' ');
    const fallback = { text: '텍스트', shape: '도형', image: '이미지', frame: '프레임', group: '그룹', note: '본문 노트' };
    const result = { id: model.id, type, label: String(label || fallback[type]).replace(/\s+/g, ' ').trim().slice(0, 100), x, y, width, height, rotate: model.rotate || 0,
      locked: Boolean(model.lockedBySelf), editableProperties: readonly || model.lockedBySelf ? [] : [...layerProperties[type]] };
    if (type === 'image') result.sourceId = model.sourceId;
    if (type === 'text') for (const key of ['color', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'textAlign']) result[key] = model[key];
    if (type === 'shape') for (const key of ['shapeType', 'fillColor', 'strokeColor', 'strokeWidth']) result[key] = model[key];
    if (type === 'frame' || type === 'note') result.fillColor = model.background;
    if (type === 'group' || type === 'frame') result.childIds = [...model.childIds];
    return result;
  };
  const sortedLayers = models => [...models].sort((a, b) => gfx().layer.compare(b, a)).map(describeLayer);
  let imageQueue = Promise.resolve();
  const imageOperation = action => {
    const result = imageQueue.then(action);
    imageQueue = result.catch(() => {});
    return result;
  };
  const prepareImage = async (file, replacingId) => {
    editable();
    if (!(file instanceof Blob) || !IMAGE_TYPES.has(file.type)) throw new Error('PNG, JPEG, WebP, GIF 이미지를 선택해 주세요.');
    if (file.size > MAX_IMAGE_BYTES) throw new Error('이미지는 개당 4 MiB 이하여야 합니다.');
    const bytes = await file.arrayBuffer();
    const id = [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map(x => x.toString(16).padStart(2, '0')).join('');
    const bitmap = await createImageBitmap(file);
    const width = Math.min(bitmap.width, 600), height = bitmap.height * width / bitmap.width;
    bitmap.close();
    editable();
    const ids = new Set(doc.getBlocksByFlavour('affine:image').filter(({model}) => model.id !== replacingId).map(({model}) => model.sourceId));
    let total = file.size;
    for (const sourceId of ids) {
      if (sourceId === id) continue;
      const blob = await collection.blobSync.get(sourceId);
      if (!blob) throw new Error(`이미지 파일이 없습니다: ${sourceId}`);
      total += blob.size;
    }
    if (total > MAX_SCENE_IMAGE_BYTES) throw new Error('한 쪽의 이미지 합계는 8 MiB 이하여야 합니다.');
    await collection.blobSync.set(id, file);
    editable();
    return { id, width, height };
  };
  const api = {
    getBlocks() {
      if (disposed) throw new Error('닫힌 편집기입니다.');
      const blocks = [];
      const visit = (model, parentId = null) => {
        const block = { id: model.id, parentId, flavour: model.flavour };
        if (typeof model.type === 'string') block.type = model.type;
        if (model.text) block.text = model.text.toString().slice(0, 2000);
        if (model.flavour === 'affine:image') block.image = { sourceId: model.sourceId, width: model.width, height: model.height };
        blocks.push(block);
        for (const child of model.children) visit(child, model.id);
      };
      visit(doc.root);
      return blocks;
    },
    getLayers() { return sortedLayers(canvasModels()); },
    getText(id) {
      if (disposed) return null;
      const selected = textModel(id);
      if (!selected) return null;
      const { model, yText, canvas } = selected;
      return { id, delta: clone(yText.toDelta()), locked: readonly || Boolean(model.lockedBySelf), ...(canvas ? Object.fromEntries(['color', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'textAlign'].map(key => [key, model[key]])) : {}) };
    },
    getSelection() {
      if (mode !== 'edgeless' || disposed) return [];
      const selected = new Set(gfx().selection.selectedIds);
      return sortedLayers(canvasModels().filter(model => selected.has(model.id)));
    },
    selectElements(ids) {
      if (disposed) throw new Error('닫힌 편집기입니다.');
      if (mode !== 'edgeless') throw new Error('요소 선택은 캔버스에서 사용할 수 있습니다.');
      if (!Array.isArray(ids) || ids.some(id => typeof id !== 'string' || !layerModel(id))) throw new Error('선택할 캔버스 요소가 없습니다.');
      gfx().selection.set({ elements: [...new Set(ids)], editing: false });
    },
    updateElementProperties(id, properties) {
      editable();
      const model = layerModel(id);
      if (!model) throw new Error('편집할 캔버스 요소가 없습니다.');
      if (!properties || typeof properties !== 'object' || Array.isArray(properties)) throw new Error('요소 속성이 올바르지 않습니다.');
      const type = layerType(model), keys = Object.keys(properties);
      for (const key of keys) {
        if (!layerProperties[type].includes(key)) throw new Error(`이 요소에서 편집할 수 없는 속성: ${key}`);
        const value = properties[key];
        if (['x', 'y', 'width', 'height', 'rotate', 'strokeWidth'].includes(key)) {
          const min = ['width', 'height'].includes(key) ? 1 : key === 'strokeWidth' ? 0 : key === 'rotate' ? -360 : -1000000;
          const max = key === 'strokeWidth' ? 100 : key === 'rotate' ? 360 : ['width', 'height'].includes(key) ? 100000 : 1000000;
          if (!Number.isFinite(value) || value < min || value > max) throw new Error(`허용 범위를 벗어난 속성: ${key}`);
        } else if (['fillColor', 'strokeColor'].includes(key) && !(typeof value === 'string' && CSS.supports('color', value))) throw new Error(`색상이 올바르지 않습니다: ${key}`);
      }
      if (!keys.length) return;
      const props = type === 'text' ? textStyle(properties) : {};
      for (const key of ['rotate', 'fillColor', 'strokeColor', 'strokeWidth']) if (Object.hasOwn(properties, key)) props[key] = properties[key];
      if ((type === 'frame' || type === 'note') && Object.hasOwn(props, 'fillColor')) { props.background = props.fillColor; delete props.fillColor; }
      if (type === 'shape' && Object.hasOwn(props, 'fillColor')) props.filled = true;
      const original = JSON.parse(model.xywh), next = [...original];
      ['x', 'y', 'width', 'height'].forEach((key, i) => { if (Object.hasOwn(properties, key)) next[i] = properties[key]; });
      if (type === 'text' && keys.some(key => ['width', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle'].includes(key))) {
        const bound = { x: next[0], y: next[1], w: next[2], h: next[3] };
        B.normalizeTextBound({ yText: model.text, fontStyle: model.fontStyle, fontWeight: model.fontWeight, fontSize: model.fontSize, fontFamily: model.fontFamily, ...props, hasMaxWidth: true }, bound, true);
        next[2] = bound.w; next[3] = bound.h; props.hasMaxWidth = true;
      }
      if (type !== 'group' && next.some((value, i) => value !== original[i])) props.xywh = JSON.stringify(next);
      const dx = next[0] - original[0], dy = next[1] - original[1];
      // Group bounds are derived. Move its real descendants in the same native transaction.
      const children = (type === 'group' || type === 'frame') && (dx || dy)
        ? [...new Map(model.descendantElements.map(child => [child.id, child])).values()].filter(child => layerType(child) !== 'group')
        : [];
      const moves = children.map(child => {
        const xywh = JSON.parse(child.xywh); xywh[0] += dx; xywh[1] += dy;
        if (!xywh.every(Number.isFinite)) throw new Error('자식 요소의 위치가 올바르지 않습니다.');
        return { child, xywh };
      });
      doc.captureSync();
      doc.transact(() => {
        for (const { child, xywh } of moves) {
          if (child.type === 'connector') child.moveTo({ x: xywh[0], y: xywh[1] });
          gfx().updateElement(child.id, { xywh: JSON.stringify(xywh) });
        }
        if (Object.keys(props).length) gfx().updateElement(model.id, props);
      });
      doc.captureSync();
    },
    async getSnapshot() {
      if (disposed) throw new Error('닫힌 편집기입니다.');
      return {...await serializeDocument(B, collection, doc), components:api.getComponents()};
    },
    async exportScene() {
      if (disposed) throw new Error('닫힌 편집기입니다.');
      const {snapshot, assets} = await api.getSnapshot();
      const scene = portableScene(snapshot, assets, color => color.startsWith('--') ? getComputedStyle(host).getPropertyValue(color).trim() : color);
      if (mode === 'edgeless') {
        const order = new Map([...gfx().gfxElements].sort((a, b) => gfx().layer.compare(a, b)).map((model, i) => [model.id, i]));
        scene.elements.sort((a, b) => order.get(a.id) - order.get(b.id));
      }
      validateScene(scene);
      return scene;
    },
    addText(text = '', options = {}) {
      return api.addRichText([{ insert: String(text) }], options);
    },
    addRichText(delta, options = {}) {
      editable(); const normalized = textDelta(delta), style = textStyle(options);
      if (mode === 'page' && Object.keys(style).length) throw new Error('문서 본문은 delta의 부분 서식으로 편집해 주세요.');
      doc.captureSync();
      if (mode === 'page') return doc.addBlock('affine:paragraph', { type: options.type || 'text', text: new B.Text(normalized) }, note().id);
      const text = new B.Y.Text(); text.applyDelta(normalized);
      return surface.addElement({ xywh: position(500, 100, options), fontFamily: 'Arial', fontSize: 24, color: '#17243b', hasMaxWidth: true, index: index(), ...options, ...style, type: 'text', text });
    },
    getSelectedText() {
      if (disposed) return null;
      let id;
      if (mode === 'edgeless') {
        const selected = gfx().selection.selectedElements;
        if (selected.length !== 1 || selected[0].type !== 'text') return null;
        id = selected[0].id;
      } else {
        const selected = editor.std.selection.value.filter(selection => selection.is('text') || selection.is('block'));
        if (selected.length !== 1 || (selected[0].to && selected[0].to.blockId !== selected[0].from.blockId)) return null;
        id = selected[0].blockId;
      }
      const selected = textModel(id);
      if (!selected) return null;
      const { model, yText, canvas } = selected;
      return { id, delta: clone(yText.toDelta()), ...(canvas ? Object.fromEntries(['color', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'textAlign'].map(key => [key, model[key]])) : {}) };
    },
    setTextContent(id, delta, options = {}) {
      editable(); const selected = textModel(id);
      if (!selected) throw new Error('텍스트 요소를 선택해 주세요.');
      // Locked text is text the document does not read back (a template's frame word): editing it would go nowhere.
      if (selected.canvas && selected.model.lockedBySelf) throw new Error('잠긴 글이라 여기서 고칠 수 없습니다. 이 글을 고치는 곳은 쪽의 안내를 따릅니다.');
      const normalized = textDelta(delta), style = textStyle(options);
      if (!selected.canvas && Object.keys(style).length) throw new Error('문서 본문은 delta의 부분 서식으로 편집해 주세요.');
      doc.captureSync();
      doc.transact(() => {
        selected.yText.delete(0, selected.yText.length);
        selected.yText.applyDelta(normalized);
        if (selected.canvas && Object.keys(style).length) surface.updateElement(id, style);
      });
      doc.captureSync();
    },
    addShape(shapeType = 'rect', options = {}) {
      editable(); if (mode !== 'edgeless') throw new Error('도형은 캔버스에서 추가할 수 있습니다.');
      if (!['rect', 'ellipse', 'triangle', 'diamond'].includes(shapeType)) throw new Error('지원하지 않는 도형입니다.');
      doc.captureSync();
      return surface.addElement({ type: 'shape', shapeType, shapeStyle: 'General', xywh: position(240, 160, options), fillColor: '#f4e4b8', strokeColor: '#9b7a3c', strokeWidth: 2, filled: true, index: index(), ...options });
    },
    addImage(file, options = {}) {
      return imageOperation(async () => {
        const { id, width, height } = await prepareImage(file);
        doc.captureSync();
        const block = doc.addBlock('affine:image', { sourceId: id, size: file.size, width, height, xywh: position(width, height, options), index: index(), ...options }, mode === 'edgeless' ? surface.id : note().id);
        doc.captureSync();
        return block;
      });
    },
    replaceImage(id, file, { name, expectedSourceId, beforeApply = () => {} } = {}) {
      editable();
      const model = doc.getBlockById(id);
      if (!model || model.flavour !== 'affine:image') throw new Error('바꿀 이미지가 없습니다.');
      if (model.lockedBySelf) throw new Error('잠긴 이미지는 바꿀 수 없습니다.');
      const originalSourceId = expectedSourceId === undefined ? model.sourceId : expectedSourceId;
      const current = () => {
        editable();
        beforeApply();
        if (doc.getBlockById(id) !== model || model.sourceId !== originalSourceId || model.lockedBySelf) throw new Error('이미지가 변경되었습니다. 현재 이미지를 다시 선택해 주세요.');
      };
      return imageOperation(async () => {
        current();
        const prepared = await prepareImage(file, id);
        current();
        doc.captureSync();
        doc.updateBlock(model, { sourceId: prepared.id, size: file.size, ...(name === undefined ? {} : { caption: String(name) }) });
        doc.captureSync();
        return id;
      });
    },
    addFrame(title = '', options = {}) {
      editable(); if (mode !== 'edgeless') throw new Error('프레임은 캔버스에서 사용할 수 있습니다.');
      doc.captureSync();
      return doc.addBlock('affine:frame', { title: new B.Text(title), xywh: position(900, 1200, options), index: index(), ...options }, surface.id);
    },
    updateElement(id, props) { editable(); doc.captureSync(); gfx().updateElement(id, props); },
    group(ids, title = '그룹') {
      editable(); if (mode !== 'edgeless') throw new Error('그룹은 캔버스에서 사용할 수 있습니다.');
      for (const id of ids) if (!gfx().getElementById(id)) throw new Error(`요소가 없습니다: ${id}`);
      doc.captureSync();
      return surface.addElement({ type: 'group', title: new B.Y.Text(title), children: new B.Y.Map(ids.map(id => [id, true])), index: index() });
    },
    reorder(id, direction) {
      editable(); if (!['front', 'forward', 'backward', 'back'].includes(direction)) throw new Error('지원하지 않는 레이어 순서입니다.');
      const element = gfx().getElementById(id); if (!element) throw new Error('요소가 없습니다.');
      doc.captureSync(); gfx().updateElement(element, { index: gfx().layer.getReorderedIndex(element, direction) }); doc.captureSync();
    },
    ...createBlockSuiteComponents({B, doc, surface, note, indexes:() => gfx().gfxElements.map(model => model.index), editable, mode:() => mode, fonts:canvasFonts}),
    setTitle(title) { editable(); doc.updateBlock(doc.root, { title: new B.Text(String(title)) }); collection.meta.setDocMeta(doc.id, { title: String(title) }); },
    undo() { editable(); doc.undo(); }, redo() { editable(); doc.redo(); },
    setMode, fitContent,
    dispose() { if (disposed) return; disposed = true; clearTimeout(resizeTimer); resizeObserver.disconnect(); selectionSubscription?.dispose(); doc.spaceDoc.off('update', changed); disposeDocumentCanvas(); editor.remove(); delete host.dataset.blocksuiteEmpty; delete host.dataset.blocksuiteMode; collection.dispose(); },
  };
  return api;
}

/**
 * One canvas per editorial section, so a template opens as pages the page panel can
 * move between instead of one tall canvas. The first page carries the document header
 * (kicker, title, subtitle) and its artwork, the last the footer. Each page keeps its
 * own origin, all naming the template's title as sourceTitle.
 */
export async function fromEditorialPages(content, { locked = () => false } = {}) {
  const validation = validateContentDocument(content);
  if (!validation.valid || content.schemaVersion !== 1) throw new Error('유효한 편집 템플릿 문서가 필요합니다.');
  const last = content.sections.length - 1;
  const pages = [];
  for (const [index, section] of content.sections.entries()) {
    const first = index === 0;
    const page = { ...content, kicker: first ? content.kicker : '', title: first ? content.title : '', subtitle: first ? content.subtitle : '',
      footer: index === last ? content.footer : '', sections: [section] };
    const title = first ? content.title : (section.title || section.alt || `${index + 1}쪽`).replace(/\s*\n\s*/g, ' ');
    const source = await fromEditorial(page, { mode: 'edgeless', frameTitle: title, headerArt: first, locked });
    pages.push({ title, ...source, origin: { ...source.origin, sourceTitle: content.title } });
  }
  return pages;
}

/** Lay out the existing editorial renderer, then transfer its separate text/art objects.
 * Decorative frames remain raster artwork; words, icons and geometry stay independent.
 */
export async function fromEditorial(content, { mode = 'edgeless', frameTitle = content.title, headerArt = true, locked = () => false } = {}) {
  const validation = validateContentDocument(content);
  if (!validation.valid || content.schemaVersion !== 1) throw new Error('유효한 편집 템플릿 문서가 필요합니다.');
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-12000px;top:0;width:1000px;height:1000px;';
  const layout = document.createElement('div');
  layout.style.cssText = 'position:fixed;left:-15000px;top:0;width:760px;';
  document.body.append(host, layout);
  let engine;
  try {
    engine = await mount(host, { mode }); engine.setTitle(frameTitle);
    if (mode === 'page') {
      const addAsset = async id => {
        if (!id) return;
        const asset = CONTENT_ASSETS.find(asset => asset.id === id);
        const response = await fetch(asset.src);
        if (!response.ok) throw new Error(`템플릿 이미지를 불러오지 못했습니다: ${id}`);
        await engine.addImage(await response.blob());
      };
      for (const text of [content.kicker, content.title, content.subtitle]) if (text) engine.addText(text);
      for (const section of content.sections) {
        if (section.title) engine.addText(section.title, { type: 'h2' });
        if (section.body) engine.addText(section.body);
        await addAsset(section.assetId);
        for (const item of section.items || []) {
          await addAsset(item.assetId);
          if (item.title) engine.addText(item.title, { type: 'h3' });
          if (item.body) engine.addText(item.body);
        }
      }
      if (content.footer) engine.addText(content.footer);
      return await engine.getSnapshot();
    }
    await contentStyles();
    layout.innerHTML = renderContentDocument(content);
    await Promise.all([...layout.querySelectorAll('img')].map(img => { img.loading = 'eager'; return img.decode(); }));
    await Promise.all(['400 20px "Content KoPub Dotum"', '700 20px "Content KoPub Dotum"', '400 20px "Content KoPub Batang"', '700 20px "Content KoPub Batang"'].map(font => document.fonts.load(font, '한글')));
    const paper = layout.querySelector('.es-content-paper');
    const origin = paper.getBoundingClientRect();
    const rect = element => { const r = element.getBoundingClientRect(); return [r.x - origin.x, r.y - origin.y, r.width, r.height]; };
    const preset = CONTENT_TEMPLATE_PRESETS.find(p => p.id === content.templateId);
    const style = getComputedStyle(paper);
    engine.addFrame(frameTitle, { xywh: JSON.stringify([0, 0, origin.width, origin.height]), background: style.getPropertyValue('--es-content-ground').trim() });
    const pageStroke = content.templateId === 'learning-editorial' ? '#b6d7d1' : '#be9452';
    engine.addShape('rect', { xywh: JSON.stringify([0, 0, origin.width, origin.height]), fillColor: style.getPropertyValue('--es-content-ground').trim(), strokeColor: pageStroke, strokeWidth: 1, lockedBySelf: true });
    const cache = new Map();
    async function load(file) {
      if (!cache.has(file)) cache.set(file, (async () => {
        const response = await fetch(`/images/content-templates/editorial/${file}`);
        if (!response.ok) throw new Error(`템플릿 이미지를 불러오지 못했습니다: ${file}`);
        return createImageBitmap(await response.blob());
      })());
      return cache.get(file);
    }
    async function canvasImage(canvas, bounds) {
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', 0.92));
      if (!blob) throw new Error('템플릿 이미지 변환에 실패했습니다.');
      return engine.addImage(blob, { xywh: JSON.stringify(bounds), width: bounds[2], height: bounds[3] });
    }
    function canvasFor(width, height) {
      const canvas = document.createElement('canvas'); canvas.width = Math.ceil(width * 2); canvas.height = Math.ceil(height * 2);
      const ctx = canvas.getContext('2d'); ctx.scale(2, 2); return [canvas, ctx];
    }
    const bitmap = await load(preset.assets.paper);
    if (content.templateId === 'saju-editorial' && headerArt) {
      const [canvas, ctx] = canvasFor(origin.width, 370);
      ctx.drawImage(bitmap, 0, 0, origin.width, bitmap.height * origin.width / bitmap.width);
      const mask = ctx.createLinearGradient(0, 240, 0, 370); mask.addColorStop(0, '#000'); mask.addColorStop(1, '#0000');
      ctx.globalCompositeOperation = 'destination-in'; ctx.fillStyle = mask; ctx.fillRect(0, 0, origin.width, 370);
      await canvasImage(canvas, [0, 0, origin.width, 370]);
    } else {
      const tileHeight = bitmap.height * 650 / bitmap.width;
      const [canvas, ctx] = canvasFor(origin.width, tileHeight);
      for (let x = 0; x < origin.width; x += 650) ctx.drawImage(bitmap, x, 0, 650, tileHeight);
      for (let y = 0; y < origin.height; y += tileHeight) {
        const height = Math.min(tileHeight, origin.height - y);
        if (height === tileHeight) await canvasImage(canvas, [0, y, origin.width, height]);
        else { const [last, lastCtx] = canvasFor(origin.width, height); lastCtx.drawImage(canvas, 0, 0, canvas.width, height * 2, 0, 0, origin.width, height); await canvasImage(last, [0, y, origin.width, height]); }
      }
    }
    engine.addShape('rect', { xywh: JSON.stringify([8, 8, origin.width - 16, origin.height - 16]), filled: false, strokeColor: pageStroke, strokeWidth: 1 });
    // Reuse the renderer's nine-slice geometry and silhouette instead of stretching its ornaments.
    async function decoration(element, role, border) {
      const bounds = rect(element), [,, width, height] = bounds, computed = getComputedStyle(element);
      const source = await load(preset.assets[role]);
      const slice = Number(computed.getPropertyValue(`--es-content-${role}-slice`));
      const [canvas, ctx] = canvasFor(width, height);
      const polygon = computed.getPropertyValue(`--es-content-${role}-clip`).trim();
      if (polygon.startsWith('polygon(')) {
        const coordinate = (value, extent) => {
          const terms = value.replace(/^calc\(|\)$/g, '').replace(/\s+/g, '').match(/[+-]?[\d.]+(?:%|px)/g) || [];
          return terms.reduce((sum, term) => sum + parseFloat(term) * (term.endsWith('%') ? extent / 100 : 1), 0);
        };
        const points = polygon.slice(8, -1).split(',').map(point => point.trim().match(/calc\([^)]*\)|[+-]?[\d.]+(?:%|px)/g));
        ctx.beginPath(); points.forEach((point, index) => { const x = coordinate(point[0], width), y = coordinate(point[1], height); if (index) ctx.lineTo(x, y); else ctx.moveTo(x, y); }); ctx.closePath(); ctx.clip();
      }
      const sx = [0, slice, source.width - slice, source.width], sy = [0, slice, source.height - slice, source.height];
      const dx = [0, border, width - border, width], dy = [0, border, height - border, height];
      for (let y = 0; y < 3; y++) for (let x = 0; x < 3; x++) ctx.drawImage(source, sx[x], sy[y], sx[x+1]-sx[x], sy[y+1]-sy[y], dx[x], dy[y], dx[x+1]-dx[x], dy[y+1]-dy[y]);
      await canvasImage(canvas, bounds);
    }
    for (const section of layout.querySelectorAll('.es-content-section--cards,.es-content-section--steps')) engine.addShape('rect', { xywh: JSON.stringify(rect(section)), filled: false, radius: 0.08, strokeColor: style.getPropertyValue('--es-content-rule').trim(), strokeWidth: 1 });
    for (const element of layout.querySelectorAll('.es-content-card')) await decoration(element, 'card', 24);
    for (const element of layout.querySelectorAll('.es-content-quote')) await decoration(element, 'summary', 27);
    for (const element of layout.querySelectorAll('.es-content-section-title')) if (getComputedStyle(element, '::before').content !== 'none') await decoration(element, 'plaque', 15);
    for (const element of layout.querySelectorAll('.es-content-steps > li,.es-content-section--exercise')) {
      const [x,y,w,h] = rect(element);
      for (const lineY of [y, y+h]) engine.addShape('rect', { xywh: JSON.stringify([x, lineY, w, 1]), fillColor: style.getPropertyValue('--es-content-rule').trim(), strokeWidth: 0 });
    }
    for (const element of layout.querySelectorAll('img')) {
      const source = await load(element.src.split('/').at(-1)); const bounds = rect(element);
      const [canvas, ctx] = canvasFor(bounds[2], bounds[3]); ctx.drawImage(source, 0, 0, bounds[2], bounds[3]); await canvasImage(canvas, bounds);
    }
    const textOrigin = { templateId: content.templateId, sourceTitle: content.title, elements: {} };
    for (const element of layout.querySelectorAll('h1,h2,h3,p,figcaption,footer,.es-content-step-number')) {
      const section = element.closest('[data-section-id]');
      const item = element.closest('.es-content-card,.es-content-steps > li,.es-content-exercise > li');
      const role = editorialTextRole({
        tag: element.tagName.toLowerCase(), classes: [...element.classList], sectionId: section?.dataset.sectionId ?? null,
        itemIndex: item ? [...item.parentElement.children].indexOf(item) : null,
      });
      const computed = getComputedStyle(element); const bounds = rect(element);
      const left = parseFloat(computed.paddingLeft), top = parseFloat(computed.paddingTop);
      bounds[0] += left; bounds[1] += top; bounds[2] -= left + parseFloat(computed.paddingRight); bounds[3] -= top + parseFloat(computed.paddingBottom);
      const fontFamily = computed.fontFamily.split(',')[0].trim().replace(/['"]/g, '');
      const id = engine.addText(element.textContent, { xywh: JSON.stringify(bounds), fontFamily, fontSize: parseFloat(computed.fontSize), fontWeight: computed.fontWeight, fontStyle: computed.fontStyle, color: computed.color, textAlign: computed.textAlign === 'center' ? 'center' : 'left' });
      if (role && typeof id === 'string') textOrigin.elements[id] = role;
      // Text the template does not read back stays where it is: locked, like the page it sits on.
      if (role && typeof id === 'string' && locked(role)) engine.updateElement(id, { lockedBySelf: true });
    }
    for (const value of cache.values()) (await value).close();
    // origin lets a canvas edited from a template be read back per section (template-saju-document.mjs).
    return { ...(await engine.getSnapshot()), origin: textOrigin };
  } finally { engine?.dispose(); host.remove(); layout.remove(); }
}
